import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "react-router-dom";

import { Modal } from "react-bootstrap";
import imageCompression from "browser-image-compression";
import { useAuthContext } from "../../../../firebase-files/hooks/useAuthContext.js";

import { format, set } from "date-fns";

import { SlLocationPin } from "react-icons/sl";
import { MdOutlineCategory } from "react-icons/md";
import { RiPlantLine } from "react-icons/ri";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { IoCloseOutline } from "react-icons/io5";

import { useDocument } from "../../../../firebase-files/hooks/useDocument";
import {
  projectFirestore,
  projectStorage,
} from "../../../../firebase-config/config";
import { useFirestore } from "../../../../firebase-files/hooks/useFirestore";

import "./AdoptedTreeDetails.scss";
import "./../plantationOneCss/PlantationOneStyle.scss";
import { Plus } from "lucide-react";

const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const MAX_FILES = 5;
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const AdoptedTreeDetails = () => {
  const { orderId, plantId } = useParams();

  const navigate = useNavigate();
  const { user } = useAuthContext();

  const { document: productDoc, error: productError } = useDocument(
    "products",
    plantId
  );

  const { document: orderDoc, error: orderError } = useDocument(
    "orders",
    orderId
  );

  const [orderHistory, setOrderHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isImgPopupOpen, setIsImgPopupOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [singleOrderHistory, setSingleOrderHistory] = useState(null);
  const [pinnedOrderHistory, setPinnedOrderHistory] = useState([]);
  const [unpinnedOrderHistory, setUnpinnedOrderHistory] = useState([]);
  const [isEditingDescUrl, setIsEditingDescUrl] = useState(false);
  const [fullDescUrl, setFullDescUrl] = useState(
    orderDoc?.full_description_url || ""
  );
  const [isPinnedCollapsed, setIsPinnedCollapsed] = useState(true);
  const [isUnpinnedCollapsed, setIsUnpinnedCollapsed] = useState(false);

  // New state for location editing
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [mapUrl, setMapUrl] = useState(orderDoc?.locationUrl);
  const [isAdmin, setIsAdmin] = useState(true); // Set this based on your auth logic

  const fetchPinnedProductHistory = useCallback(() => {
    if (!orderDoc?.userId) return;

    const unsubscribe = projectFirestore
      .collection("order_history")
      .where("orderId", "==", orderId)
      .where("userId", "==", orderDoc.userId)
      .where("isPinned", "==", true)
      .orderBy("createdAt", "desc")
      .onSnapshot(
        (snapshot) => {
          if (snapshot.empty) {
            setPinnedOrderHistory([]);
          } else {
            const history = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }));
            setPinnedOrderHistory(history);
          }
          setIsLoadingHistory(false);
        },
        (error) => {
          console.error("Error fetching history:", error);
          setIsLoadingHistory(false);
        }
      );

    return unsubscribe;
  }, [orderDoc?.userId, orderId]);

  const fetchUnpinnedProductHistory = useCallback(() => {
    if (!orderDoc?.userId) return;

    const unsubscribe = projectFirestore
      .collection("order_history")
      .where("orderId", "==", orderId)
      .where("userId", "==", orderDoc.userId)
      .where("isPinned", "==", false)
      .orderBy("createdAt", "desc")
      .onSnapshot(
        (snapshot) => {
          if (snapshot.empty) {
            setUnpinnedOrderHistory([]);
          } else {
            const history = snapshot.docs.map((doc) => ({
              ...doc.data(),
              id: doc.id,
            }));
            setUnpinnedOrderHistory(history);
          }
          setIsLoadingHistory(false);
        },
        (error) => {
          console.error("Error fetching history:", error);
          setIsLoadingHistory(false);
        }
      );

    return unsubscribe;
  }, [orderDoc?.userId, orderId]);

  useEffect(() => {
    if (orderDoc?.userId) {
      const unsubscribe = fetchPinnedProductHistory();
      return () => unsubscribe && unsubscribe();
    }
  }, [fetchPinnedProductHistory]);

  useEffect(() => {
    if (orderDoc?.userId) {
      const unsubscribe = fetchUnpinnedProductHistory();
      return () => unsubscribe && unsubscribe();
    }
  }, [fetchUnpinnedProductHistory]);

  const handleDelete = async (post) => {
    console.log("delete", post);

    try {
      for (let image of post.images) {
        const storageRef = projectStorage.refFromURL(image.url);
        await storageRef.delete();
      }
      await projectFirestore.collection("order_history").doc(post.id).delete();
      console.log("deleted");
    } catch (error) {
      console.log(error);
    }
  };
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleExpand = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };
  const extractGoogleMapsEmbedUrl = (input) => {
    if (!input || typeof input !== "string") return "";

    const iframeMatch = input.match(/src="([^"]+)"/);
    if (iframeMatch) {
      return iframeMatch[1];
    }
    if (input.includes("https://www.google.com/maps/embed")) {
      return input;
    }
    return "";
  };

  const handleSaveLocation = async () => {
    try {
      const cleanedUrl = extractGoogleMapsEmbedUrl(mapUrl);

      if (!cleanedUrl) {
        alert("Please paste a valid Google Maps iframe or embed URL.");
        return;
      }

      const querySnapshot = await projectFirestore
        .collection("order_history")
        .where("orderId", "==", orderId)
        .get();

      const batch = projectFirestore.batch();
      querySnapshot.forEach((doc) => {
        const docRef = projectFirestore.collection("order_history").doc(doc.id);
        batch.update(docRef, {
          locationUrl: cleanedUrl,
          lastUpdated: new Date(),
        });
      });

      await batch.commit();

      await projectFirestore.collection("orders").doc(orderId).update({
        locationUrl: cleanedUrl,
        lastUpdated: new Date(),
      });

      setIsEditingLocation(false);
      console.log("✅ Cleaned Google Maps URL saved in database.");
    } catch (error) {
      console.error("Error saving location:", error);
      alert("Error saving location: " + error.message);
    }
  };

  const isLoading = !productDoc || !orderDoc || isLoadingHistory;
  useEffect(() => {
    // const checkCamera = async () => {
    //   try {
    //     const devices = await navigator.mediaDevices.enumerateDevices();
    //     const cameras = devices.filter(device => device.kind === 'videoinput');
    //     console.log('Available Cameras:', cameras);
    //     if (cameras.length > 0) {
    //       const stream = await navigator.mediaDevices.getUserMedia({
    //         video: true
    //       });
    //       console.log('Camera access successful!');
    //       stream.getTracks().forEach(track => track.stop()); // क्लीनअप
    //     }
    //   } catch (error) {
    //     console.error('Camera Error:', error);
    //   }
    // };
    // checkCamera();
  }, []);
  return (
    <section className="p-2 p-sm-2 p-md-4 my-plant-detail position-relative">
      <div className="p-2 p-sm-3 my-plant-detail-container">
        <div className="my-plant-detail-left">
          <div className="my-plant-detail-card">
            <div className="my-plant-detail-header">
              <h5>{productDoc?.name}</h5>
              {productDoc?.images?.length > 0 && (
                <Link to={`/product-details-two/${productDoc.id}`}>
                  <div className="plantation-card-image">
                    <img
                      src={
                        typeof productDoc.images?.[0] === "string"
                          ? productDoc.images[0]
                          : productDoc.images?.[0]?.url
                      }
                      alt={productDoc.name || "Plant"}
                      loading="lazy"
                      style={{
                        width: "100%",
                        maxWidth: "450px", // jitna container ho, utna space le
                        height: "auto",
                        display: "block",
                        margin: "1rem auto 1.5rem auto", // center & margin below image
                        borderRadius: "12px",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
                        cursor: "pointer",
                      }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "/no-image-available.png"; // fallback image
                      }}
                    />
                  </div>
                </Link>
              )}
            </div>
            <div className="my-plant-detail-body">
              <div className="my-plant-info">
                {/* <p>
                  <MdOutlineCategory className="plant-info-icon" />
                  <span className="fw-bold">
                    Category:{" "}
                    <span className="fw-normal">{productDoc?.category[0]}</span>
                  </span>
                </p> */}
                {/* {productDoc?.subscriptionDuration && productDoc?.subscriptionDurationType && (
                  <p>
                    <RiPlantLine className="plant-info-icon" />
                    <span className="fw-bold">
                      Tree Subscription will Expires on:{" "}
                      <span className="fw-normal">
                        {orderDoc?.createdAt
                          ? format(
                            (() => {
                              const date = new Date(orderDoc.createdAt.seconds * 1000);
                              const duration = parseInt(productDoc.subscriptionDuration);
                              const type = productDoc.subscriptionDurationType.toLowerCase();

                              if (type === "year") {
                                date.setFullYear(date.getFullYear() + duration);
                              } else if (type === "month") {
                                date.setMonth(date.getMonth() + duration);
                              } else if (type === "day") {
                                date.setDate(date.getDate() + duration);
                              }

                              return date;
                            })(),
                            "MMM dd, yyyy"
                          )
                          : "Loading..."}
                      </span>
                    </span>

                  </p>
                )} */}
                <p>
                  <RiPlantLine className="plant-info-icon" />
                  <span className="fw-bold">
                    Adopted on:{" "}
                    <span className="fw-normal">
                      {orderDoc?.createdAt
                        ? new Date(
                            orderDoc.createdAt.seconds * 1000
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : "Loading..."}
                    </span>
                  </span>
                </p>
              </div>
              <div
                className="my-4 w-100"
                style={{
                  border: "0.5px solid #D9D9D9",
                  opracity: "0.5",
                }}
              />
            </div>

            <div className="my-plant-footer">
              {/* Short Description */}
              <p
                className="plantation-card-description"
                dangerouslySetInnerHTML={{
                  __html: productDoc?.short_description || "",
                }}
              />

              {/* Full Description Section - Added inside the same card */}
              <div className="mt-3">
                <button
                  className="Button_box"
                  onClick={() =>
                    navigate(`/product-details-two/${productDoc?.id}`)
                  } // Updated route
                >
                  Read More
                </button>
              </div>
            </div>
          </div>
          {/* Only show location section if there is a location or admin is editing */}
          {(orderDoc?.locationUrl ||
            mapUrl ||
            (user && user.role === "admin")) && (
            <div className="my-plant-detail-card gap-4">
              {/* Location heading */}
              <div className="d-flex justify-content-between align-items-center">
                <h5>Location</h5>
                {user && user.role === "admin" && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setIsEditingLocation(!isEditingLocation)}
                  >
                    {isEditingLocation ? "Cancel" : "Edit"}
                  </button>
                )}
              </div>

              {/* Location content */}
              {(orderDoc?.locationUrl || mapUrl || isEditingLocation) && (
                <>
                  {isEditingLocation ? (
                    <div className="location-edit-container">
                      <input
                        type="text"
                        className="form-control mb-2"
                        value={mapUrl}
                        onChange={(e) => setMapUrl(e.target.value)}
                        placeholder="Paste Google Maps embed URL here"
                      />
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={handleSaveLocation}
                      >
                        Save Location
                      </button>

                      <div className="mt-3">
                        <h6>Preview:</h6>
                        <iframe
                          src={extractGoogleMapsEmbedUrl(mapUrl)}
                          frameBorder="0"
                          width="100%"
                          height={300}
                          style={{ border: 0 }}
                          allowFullScreen
                          loading="lazy"
                          referrerPolicy="no-referrer-when-downgrade"
                        />
                      </div>
                    </div>
                  ) : (
                    <iframe
                      src={orderDoc?.locationUrl || mapUrl}
                      frameBorder="0"
                      width="100%"
                      height={400}
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="my-plant-detail-right">
          <div className="my-plant-detail-right-header">
            <div>
              <h4>{productDoc?.name} Growth Journey </h4>
            </div>
            <div className="">
              {user && user.role === "admin" && (
                <button
                  onClick={() => {
                    setShowHistoryModal(true);
                  }}
                  className="update-btn"
                >
                  Track Progress
                </button>
              )}
            </div>
          </div>

          {!isLoading ? (
            <div className="my-plant-detail-right-body mt-3">
              {pinnedOrderHistory.length > 0 && (
                <h5 className="static-title">
                  {" "}
                  🌱 Your Tree’s First Day in Nature
                </h5>
              )}

              {pinnedOrderHistory.length > 0 && (
                <div className="pinned-section">
                  <div
                    className="section-header"
                    onClick={() => setIsPinnedCollapsed(!isPinnedCollapsed)}
                  >
                    <h5>
                      {pinnedOrderHistory[0]?.title || "Planted with Love"}
                    </h5>
                    <div className="header-right">
                      <span className="post-date">
                        {format(
                          pinnedOrderHistory[0].createdAt.toDate(),
                          "MMM dd, yyyy"
                        )}
                      </span>
                      <span className="collapse-icon">
                        {isPinnedCollapsed ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronUp size={16} />
                        )}
                      </span>
                    </div>
                  </div>
                  {!isPinnedCollapsed && (
                    <div className="pinned-content">
                      {pinnedOrderHistory.map((item, index) => (
                        <OrderHistoryCard
                          key={index + item.id}
                          item={item}
                          handleDelete={handleDelete}
                          isAdmin={user && user.role === "admin"}
                          onClick={() => setSingleOrderHistory(item)}
                          setShowHistoryModal={setShowHistoryModal}
                          setIsEditing={setIsEditing}
                          setIsImgPopupOpen={setIsImgPopupOpen}
                          setSelectedIndex={setSelectedIndex}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {unpinnedOrderHistory.length > 0 && (
                <h5 className="static-title">
                  Watch It Flourish – Your Tree's Next Chapter
                </h5>
              )}

              {unpinnedOrderHistory.length > 0 && (
                <>
                  {/* Always show latest unpinned post (index 0) without collapse */}
                  <div className="unpinned-card">
                    <div className="card-header">
                      <h5>
                        {unpinnedOrderHistory[0].title || "Watch it Flourish"}
                      </h5>
                      <div className="header-right">
                        <span className="post-date">
                          {format(
                            unpinnedOrderHistory[0].createdAt.toDate(),
                            "MMM dd, yyyy"
                          )}
                        </span>
                      </div>
                    </div>
                    <OrderHistoryCard
                      item={unpinnedOrderHistory[0]}
                      handleDelete={handleDelete}
                      isAdmin={user && user.role === "admin"}
                      onClick={() =>
                        setSingleOrderHistory(unpinnedOrderHistory[0])
                      }
                      setShowHistoryModal={setShowHistoryModal}
                      setIsEditing={setIsEditing}
                      setIsImgPopupOpen={setIsImgPopupOpen}
                      setSelectedIndex={setSelectedIndex}
                    />
                  </div>

                  {/* Show older posts (index > 0) as collapsible */}
                  {unpinnedOrderHistory.slice(1).map((item, index) => {
                    const actualIndex = index + 1; // since we sliced the array
                    const isExpanded = expandedIndex === actualIndex;

                    return (
                      <div className="unpinned-card" key={item.id}>
                        <div
                          className="card-header"
                          onClick={() => toggleExpand(actualIndex)}
                        >
                          <h5>{item.title || "Watch it Flourish"}</h5>
                          <div className="header-right">
                            <span className="post-date">
                              {format(item.createdAt.toDate(), "MMM dd, yyyy")}
                            </span>
                            <span className="collapse-icon">
                              {isExpanded ? (
                                <ChevronUp size={16} />
                              ) : (
                                <ChevronDown size={16} />
                              )}
                            </span>
                          </div>
                        </div>

                        {isExpanded && (
                          <OrderHistoryCard
                            item={item}
                            handleDelete={handleDelete}
                            isAdmin={user && user.role === "admin"}
                            onClick={() => setSingleOrderHistory(item)}
                            setShowHistoryModal={setShowHistoryModal}
                            setIsEditing={setIsEditing}
                            setIsImgPopupOpen={setIsImgPopupOpen}
                            setSelectedIndex={setSelectedIndex}
                          />
                        )}
                      </div>
                    );
                  })}
                </>
              )}

              {pinnedOrderHistory.length === 0 &&
                unpinnedOrderHistory.length === 0 && (
                  <div className="no-updates-wrapper">
                    <HiOutlineInformationCircle className="no-updates-icon" />
                    <p className="no-updates-text">
                      Thanks for adopting the {productDoc?.name} tree! 🌱 We’re
                      working on preparing the site and will soon share its
                      location with the first growth update.!{" "}
                    </p>
                  </div>
                )}
            </div>
          ) : (
            <p>Loading...</p>
          )}
        </div>
        <ImagePopover
          isOpen={isImgPopupOpen}
          onClose={() => setIsImgPopupOpen(false)}
          data={singleOrderHistory}
          currentIndex={selectedIndex}
        />
        <AddHistory
          showHistoryModal={showHistoryModal}
          setShowHistoryModal={setShowHistoryModal}
          orderId={orderId}
          userId={orderDoc?.userId}
          productName={productDoc?.name}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          postId={singleOrderHistory}
        />
      </div>
    </section>
  );
};

export default AdoptedTreeDetails;

const OrderHistoryCard = ({
  item,
  handleDelete,
  isAdmin,

  setShowHistoryModal,
  setIsEditing,
  setSelectedIndex,
  setIsImgPopupOpen,
  ...props
}) => {
  return (
    <div className="my-plant-detail-right-card p-3" {...props}>
      <div className="my-plant-detail-right-card-header">
        <div className="d-flex flex-column">
          {/* <h5>{item.title}</h5> */}

          {/* <span className="post-date">
            {format(item.createdAt.toDate(), "MMM dd, yyyy")}
          </span> */}
        </div>
        {isAdmin && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{
              gap: "2px",
            }}
          >
            {/* edit svg */}

            <span
              className="d-flex align-items-center"
              onClick={() => {
                setShowHistoryModal(true);
                setIsEditing(true);
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="edit-icon"
              >
                <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />
              </svg>
            </span>
            {/* trash svg */}
            <span
              className="d-flex align-items-center"
              onClick={() => handleDelete(item)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="trash-icon"
              >
                <g className="trash-lid">
                  <path d="M3 6h18" />
                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                </g>

                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                <line x1="10" x2="10" y1="11" y2="17" />
                <line x1="14" x2="14" y1="11" y2="17" />
              </svg>
            </span>
          </div>
        )}
      </div>

      <div className="my-plant-detail-right-card-body">
        <div className="plant-img-bento-layout">
          <div
            className={`plant-img-bento-grid position-relative layout-${Math.min(
              item.images.length,
              4
            )} `}
          >
            {item.images.length > 0 &&
              item.images.slice(0, 4).map((img, index) => (
                <BentoGridItem
                  key={index + img?.url}
                  index={index}
                  length={item?.images?.length}
                  image={img?.url}
                  onClick={() => {
                    setIsImgPopupOpen(true);
                    setSelectedIndex(index);
                  }}
                />
              ))}
          </div>
          {/* <div className="d-flex justify-content-end">
                          <Link to={"#"} className="view-all-link">
                            View all images
                          </Link>
                        </div> */}
        </div>
        {item.note && (
          <div className="card-body-description p-2">
            <p>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                className="user-avatar"
              >
                <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" />
                <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
              </svg>
            </p>
            <div className="d-flex flex-column align-items-start">
              <p className="caretaker-note">Caretaker Note: </p>
              <p>{item.note}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
const BentoGridItem = ({ className = "", index, length, image, ...props }) => {
  return (
    <div
      className={`plant-img-bento-grid-item ${className}`}
      style={{
        border: "0.5px solid #0000004d",
      }}
      {...props}
    >
      <img
        src={image}
        alt="Plant"
        style={{ width: "100%", height: "100%" }}
        loading="lazy"
      />
      {length > 4 && index === 3 && (
        <div
          className="plant-img-bento-grid-item-more"
          style={{
            border: "0.5px solid #0000004d",
          }}
        >
          <p className="position-absolute">+{length - 4}</p>
        </div>
      )}
    </div>
  );
};

const Max_Word = 200;

const AddHistory = ({
  showHistoryModal,
  setShowHistoryModal,
  orderId,
  userId,
  productName,
  isEditing,
  setIsEditing,
  postId,
}) => {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [docUrl, setDocUrl] = useState([]);
  const [previewUrl, setPreviewUrl] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [imgUploading, setImgUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [deletedImages, setDeletedImages] = useState([]);
  const [wordCount, setWordCount] = useState(0);
  const uploadRef = useRef(null);
  const [openCameraModal, setOpenCameraModal] = useState(false);
  const { addDocument, updateDocument } = useFirestore("order_history");
  useEffect(() => {
    if (isEditing && postId) {
      setIsEditing(true);
      setTitle(postId?.title || "");
      setNote(postId?.note || "");
      setPreviewUrl(postId?.images || []);
      setDocUrl(postId?.images || []);
    }
  }, [postId, setIsEditing, isEditing, userId, orderId]);

  const checkCollection = async () => {
    const collectionRef = projectFirestore.collection("order_history");
    const snapshot = await collectionRef
      .where("orderId", "==", orderId)
      .limit(1)
      .get();
    return snapshot.empty;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (!title || previewUrl.length === 0) {
        alert(`please enter ${!title ? "title" : "docUrl"}`);
        return;
      }

      const imagesUrl = [];

      for (let file of docUrl) {
        console.log("file", file);
        if (file?.url?.startsWith("https://")) {
          imagesUrl.push(file);
          continue;
        }
        const storageRef = projectStorage.ref(
          `order_history/${userId}/${orderId}/${format(
            Date.now(),
            "yyyy-MM-dd HH:mm:ss"
          )}_${file.name}`
        );
        const uploadTask = await storageRef.put(file);
        const downloadURL = await uploadTask.ref.getDownloadURL();
        imagesUrl.push({ url: downloadURL, name: file.name, size: file.size });
      }

      let newHistory = {
        orderId,
        userId,
        title,
        note,
        images: imagesUrl,
      };
      console.log("newHistory", newHistory);
      if (!isEditing) {
        newHistory.isPinned = await checkCollection();
        // console.log("newHistory", newHistory);
        await addDocument(newHistory);
        setDocUrl([]);
        setPreviewUrl([]);
        setTitle("");
        setNote("");
        setShowHistoryModal(false);
      } else {
        for (let image of deletedImages) {
          const storageRef = projectStorage.refFromURL(image.url);
          await storageRef.delete();
        }
        await updateDocument(postId.id, newHistory);
        //toast.success("Post updated successfully!");
        setShowHistoryModal(false);
        //clearField();
      }
    } catch (error) {
      console.error("handleSubmit", error);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadDocuments = async (files) => {
    try {
      const uploadedFiles = [];
      const previewFilesUrl = [];
      setImgUploading(true);
      for (let file of files) {
        // `        console.log(file instanceof File);`
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1024,
          useWebWorker: true,
        });

        const compressedFileData = {
          ...compressedFile,
          size: compressedFile.size,
          type: compressedFile.type,
          url: URL.createObjectURL(compressedFile),
        };
        console.log("compressedFileData", compressedFileData);
        uploadedFiles.push(compressedFile);
        previewFilesUrl.push(compressedFileData);
      }
      setDocUrl((prev) => [...prev, ...uploadedFiles]);
      setPreviewUrl((prev) => [...prev, ...previewFilesUrl]);

      console.log("Uploaded URLs:", uploadedFiles);
    } catch (error) {
      console.error("Error uploading one or more documents:", error);
    } finally {
      setImgUploading(false);
    }
  };

  const handleRemoveImage = async (index) => {
    try {
      setPreviewUrl((prev) => {
        const updated = [...prev];
        const removed = updated.splice(index, 1);

        if (isEditing && removed?.url?.startsWith("https://")) {
          setDeletedImages((prev) => [...prev, removed]);
        } else if (removed?.url) {
          URL.revokeObjectURL(removed.url);
        }
        setDocUrl((prevDoc) => {
          const updatedDoc = [...prevDoc];
          updatedDoc.splice(index, 1);
          return updatedDoc;
        });
        return updated;
      });

      console.log("Image deleted successfully");
    } catch (error) {
      console.error("Error deleting image:", error);
    }
  };

  const handleFileChange = useCallback(
    async (files) => {
      try {
        if (files.length === 0) {
          throw new Error("Please upload at least one file.");
        }
        if (
          files.length >= MAX_FILES &&
          previewUrl.length + files.length >= MAX_FILES
        ) {
          throw new Error("You can only upload up to 5 files.");
        }
        console.log(previewUrl.length + files.length);

        for (let i = 0; i < files.length; i++) {
          if (!allowedTypes.includes(files[i].type)) {
            throw new Error(`${files[i].type} not allowed`);
          }
          if (files[i].size > MAX_FILE_SIZE) {
            throw new Error(`you can upload file upto 5MB in size`);
          }
          if (files[i].size > MAX_FILE_SIZE) {
            throw new Error(`you can upload file upto 5MB in size`);
          }
        }

        await uploadDocuments(files);
      } catch (err) {
        console.error(err);
      }
    },
    [previewUrl.length]
  );

  const clearField = () => {
    setTitle("");
    setNote("");
    setPreviewUrl([]);
    setDocUrl([]);
    if (isEditing) {
      setDeletedImages([]);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragOver(false);
      handleFileChange(e.dataTransfer.files);
    },
    [handleFileChange]
  );
  return (
    <>
      <Modal
        show={showHistoryModal}
        onHide={() => {
          setShowHistoryModal(false);
          if (isEditing) {
            setIsEditing(false);
          }
          clearField();
        }}
        centered
        size="lg"
        dialogClassName={`
        ${showHistoryModal ? "fade-in" : "fade-out"} 
        update-timeline-modal`}
      >
        <Modal.Header className="border-0 timeline-modal-header">
          <h2> {isEditing ? `update` : `Post update for ${productName}`}</h2>
          <button
            className="timeline-close-btn"
            onClick={() => {
              setShowHistoryModal(false);
              if (isEditing) {
                setIsEditing(false);
              }
              clearField();
            }}
          >
            <IoCloseOutline className="timeline-close-icon" />
          </button>
        </Modal.Header>
        <Modal.Body className="timeline-modal-body">
          <form className="d-flex flex-column gap-3" onSubmit={handleSubmit}>
            <div className="d-flex flex-column gap-3">
              <div>
                <label
                  className="timeline-modal-label"
                  style={{
                    marginBottom: "8px",
                  }}
                >
                  Title <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-input"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  disabled={isLoading}
                />
              </div>
              <div>
                <div className="d-flex justify-content-between align-items-center">
                  <label className="timeline-modal-label">Description </label>
                  <span>
                    {wordCount}/{Max_Word} words
                  </span>
                </div>
                <textarea
                  type="text"
                  className="form-input form-textarea"
                  value={note}
                  onChange={(e) => {
                    // if (!e.target.value) return;
                    if (e.target.value.length > Max_Word) return;
                    setWordCount(e.target.value.length);
                    setNote(e.target.value);
                  }}
                  disabled={isLoading}
                />
              </div>
              <div>
                <div className="d-flex justify-content-between align-items-center">
                  <label className="timeline-modal-label">
                    Upload Photos <span className="text-danger">*</span>{" "}
                  </label>
                  <span>
                    ({previewUrl.length}/{MAX_FILES} images)
                  </span>
                </div>

                <div
                  className="timeline-upload-area"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  style={
                    imgUploading || isLoading || previewUrl.length >= MAX_FILES
                      ? {
                          opacity: "0.8",
                          cursor: "not-allowed",
                          backgroundColor: "rgba(0, 0, 0, 0.1)",
                          borderColor: "rgba(0, 0, 0, 0.1)",
                        }
                      : {
                          opacity: "1",
                          cursor: "default",
                        }
                  }
                >
                  <div
                    className={`d-flex flex-column gap-3 justify-content-center align-items-center p-2`}
                  >
                    {imgUploading ? (
                      <div>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="rotate"
                          style={{
                            color: "#00c951",
                          }}
                        >
                          <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                        </svg>
                      </div>
                    ) : (
                      <>
                        <div className="d-flex flex-column gap-3 justify-content-center align-items-center">
                          <div
                            className={`upload-icon ${
                              isLoading || previewUrl.length >= MAX_FILES
                                ? "disabled"
                                : ""
                            }`}
                            onClick={() => uploadRef.current?.click()}
                            style={{ cursor: "pointer" }}
                          >
                            {/* Upload icon */}
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M12 3v12" />
                              <path d="m17 8-5-5-5 5" />
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            </svg>
                          </div>
                          <h6>
                            {isDragOver
                              ? "Drop your images here!"
                              : "Upload your photos"}
                          </h6>
                        </div>
                        <div className="d-flex flex-column gap-2 gap-sm-3 justify-content-center align-items-center">
                          <div className="image-upload-method">
                            You can upload up to 5 more images. Drag and drop
                            them here,{" "}
                            <button
                              type="button"
                              onClick={() => uploadRef.current?.click()}
                              className="upload-method-btn"
                            >
                              Browse files
                            </button>{" "}
                            , or{" "}
                            <button
                              type="button"
                              className="upload-method-btn"
                              onClick={() => {
                                setOpenCameraModal(true);
                                setShowHistoryModal(false);
                              }}
                            >
                              Take a photo
                            </button>{" "}
                            using your camera.
                          </div>

                          <p className="image-type-info">
                            PNG, JPG, GIF, WEBP • Max 5MB each • Up to{" "}
                            {MAX_FILES} images
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <input
                  ref={uploadRef}
                  type="file"
                  className="form-upload-input"
                  onChange={(e) => handleFileChange(e.target.files)}
                  multiple
                  disabled={
                    imgUploading || isLoading || previewUrl.length >= MAX_FILES
                  }
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                />
              </div>

              {previewUrl.length > 0 && (
                <div className="d-flex flex-column gap-2 flex-wrap preview-images">
                  <h6 className="timeline-modal-label">Preview</h6>
                  <div className="images-grid">
                    {previewUrl.map((data, index) => {
                      const calculateMB = (data.size / 1024 / 1024).toFixed(2);
                      return (
                        <div key={`${data}+${index}`} className="preview-ele">
                          <div className="position-relative preview-image">
                            <img
                              loading="lazy"
                              src={data.url}
                              alt={`image/${data.name}`}
                            />
                            <span
                              className="images-remove"
                              style={{ cursor: "pointer" }}
                              onClick={() => handleRemoveImage(index)}
                            >
                              {/* trash icon */}
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="remove-image-icon"
                              >
                                <g>
                                  <path d="M3 6h18" />
                                  <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                </g>
                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                              </svg>
                            </span>
                          </div>

                          <div className="preview-image-info p-2">
                            <h6>{data.name}</h6>
                            <p>{calculateMB} MB</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <button
              className="update-btn"
              disabled={isLoading}
              style={
                isLoading
                  ? {
                      opacity: "0.2",
                      cursor: "not-allowed",
                    }
                  : {
                      opacity: "1",
                      cursor: "pointer",
                    }
              }
            >
              {isLoading ? "Uploading..." : "Upload"}
            </button>
          </form>
        </Modal.Body>
      </Modal>
      {/* <Modal
        show={openCameraModal}
        onHide={() => {
          setOpenCameraModal(false);
          setShowHistoryModal(true);
        }}
        centered
        size="lg"
        dialogClassName={`
        ${openCameraModal ? "fade-in" : "fade-out"} 
        update-timeline-modal`}
      >
        <Modal.Header closeButton>
          <Modal.Title>Take a photo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ImageCapture
          // onCapture={handleCapture}
          // onClose={() => setOpenCameraModal(false)}
          />
        </Modal.Body>
      </Modal> */}
      <Modal
        show={openCameraModal}
        onHide={() => {
          setOpenCameraModal(false);
          setShowHistoryModal(true);
        }}
        centered
        size="lg"
        dialogClassName={`${
          openCameraModal ? "fade-in" : "fade-out"
        } update-timeline-modal`}
      >
        <Modal.Header closeButton>
          <Modal.Title>Take a photo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ImageCapture
            onCapture={(files) => {
              handleFileChange(files);
              setOpenCameraModal(false);
              setShowHistoryModal(true);
            }}
            onClose={() => {
              setOpenCameraModal(false);
              setShowHistoryModal(true);
            }}
          />
        </Modal.Body>
      </Modal>
    </>
  );
};

const ImagePopover = ({ isOpen, onClose, data, currentIndex = 0 }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(currentIndex);
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const imageRef = useRef(null);

  useEffect(() => {
    setCurrentImageIndex(currentIndex);
  }, [currentIndex]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const constrainPosition = useCallback((newPosition, currentScale) => {
    if (!imageRef.current || currentScale <= 1) {
      return { x: 0, y: 0 };
    }

    const img = imageRef.current;
    const container = img.parentElement;

    if (!container) return { x: 0, y: 0 };

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    const { naturalWidth, naturalHeight } = img;

    const containerRatio = containerWidth / containerHeight;
    const imageRatio = naturalWidth / naturalHeight;

    let fittedWidth, fittedHeight;
    if (imageRatio > containerRatio) {
      fittedWidth = containerWidth;
      fittedHeight = (naturalHeight / naturalWidth) * containerWidth;
    } else {
      fittedHeight = containerHeight;
      fittedWidth = (naturalWidth / naturalHeight) * containerHeight;
    }

    const scaledWidth = fittedWidth * currentScale;
    const scaledHeight = fittedHeight * currentScale;

    const maxOffsetX = Math.max(0, (scaledWidth - containerWidth) / 2);
    const maxOffsetY = Math.max(0, (scaledHeight - containerHeight) / 2);

    return {
      x: Math.max(-maxOffsetX, Math.min(maxOffsetX, newPosition.x)),
      y: Math.max(-maxOffsetY, Math.min(maxOffsetY, newPosition.y)),
    };
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      if (scale <= 1) return;

      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      setInitialPosition(position);
    },
    [scale, position]
  );

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging || scale <= 1) return;

      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;

      const newPosition = {
        x: initialPosition.x + deltaX,
        y: initialPosition.y + deltaY,
      };

      setPosition(constrainPosition(newPosition, scale));
    },
    [isDragging, scale, dragStart, initialPosition, constrainPosition]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleTouchStart = useCallback(
    (e) => {
      if (scale <= 1) return;

      e.preventDefault();
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX, y: touch.clientY });
      setInitialPosition(position);
    },
    [scale, position]
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!isDragging || scale <= 1 || e.touches.length === 0) return;

      e.preventDefault();
      const touch = e.touches[0];
      const deltaX = touch.clientX - dragStart.x;
      const deltaY = touch.clientY - dragStart.y;

      const newPosition = {
        x: initialPosition.x + deltaX,
        y: initialPosition.y + deltaY,
      };

      setPosition(constrainPosition(newPosition, scale));
    },
    [isDragging, scale, dragStart, initialPosition, constrainPosition]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove, {
        passive: false,
      });
      document.addEventListener("mouseup", handleMouseUp, {
        passive: false,
      });
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd, {
        passive: false,
      });
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    isDragging,
    handleMouseMove,
    handleMouseUp,
    handleTouchMove,
    handleTouchEnd,
  ]);

  useEffect(() => {
    const container = containerRef.current;
    const selectedItem = itemRefs.current[currentImageIndex];

    if (container && selectedItem) {
      const containerRect = container.getBoundingClientRect();
      const itemRect = selectedItem.getBoundingClientRect();

      const itemCenter = itemRect.left + itemRect.width / 2;
      const containerCenter = containerRect.left + containerRect.width / 2;
      const offset = itemCenter - containerCenter;

      container.scrollTo({ left: offset, behavior: "smooth" });
    }
  }, [currentImageIndex]);

  const handleWheelEvent = (e) => {
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newScale = Math.max(0.3, Math.min(2, scale + delta));
    setScale(newScale);
    if (newScale <= 1) {
      setPosition({ x: 0, y: 0 });
    } else {
      setPosition((prev) => constrainPosition(prev, newScale));
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`image-popover ${isOpen ? "fade-in" : "fade-out"} `}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="image-popover-header">
        <div className="d-flex justify-content-center align-items-center gap-2">
          <button
            className="popover-close-btn"
            onClick={() => {
              onClose();
            }}
            aria-label="Close image viewer"
          >
            <IoCloseOutline className="popover-close-icon" />
          </button>
          <div className=" popover-header-text">
            <span>{data.title}</span>
            <span className="popover-image-date">
              {format(data.createdAt.toDate(), "MMM dd, yyyy, hh:mm a")}
            </span>
          </div>
        </div>

        <div className="image-popover-zoom">
          <button
            onClick={() => {
              const newScale = Math.max(0.3, scale - 0.1);
              setScale(newScale);
              if (newScale <= 1) {
                setPosition({ x: 0, y: 0 });
              } else {
                setPosition((prev) => constrainPosition(prev, newScale));
              }
            }}
            disabled={scale <= 0.3}
            className="image-popover-zoom-btn"
          >
            {/* zoom out svg */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="popover-zoom-icon"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" x2="16.65" y1="21" y2="16.65" />
              <line x1="8" x2="14" y1="11" y2="11" />
            </svg>
          </button>
          <span style={{ color: "#d4d4d4", fontSize: "14px" }}>
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={() => {
              const newScale = Math.min(2, scale + 0.1);

              setScale(newScale);
              if (newScale > 1) {
                setPosition((prev) => {
                  console.log(prev);
                  return constrainPosition(prev, newScale);
                });
              }
            }}
            disabled={scale >= 2}
            className="image-popover-zoom-btn"
          >
            {/* zoom in svg */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="popover-zoom-icon"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" x2="16.65" y1="21" y2="16.65" />
              <line x1="11" x2="11" y1="8" y2="14" />
              <line x1="8" x2="14" y1="11" y2="11" />
            </svg>
          </button>
        </div>
      </div>

      <div className="popover-body" onWheel={handleWheelEvent}>
        <div>
          <div className="image-carousel prev">
            {/* left arrow svg */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="image-carousel-icon"
              style={{
                cursor: currentImageIndex === 0 ? "not-allowed" : "pointer",
                opacity: currentImageIndex === 0 ? 0.5 : 1,
              }}
              onClick={() => {
                if (currentImageIndex === 0) return;
                setCurrentImageIndex(currentImageIndex - 1);
                setScale(1);
                setPosition({ x: 0, y: 0 });
              }}
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </div>
        </div>

        <div
          className="popover-image-container"
          style={{ touchAction: "none" }}
        >
          <img
            src={data.images[currentImageIndex].url}
            ref={imageRef}
            alt=""
            className="popover-image"
            style={{
              transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
              transition: isDragging ? "none" : "transform 0.2s ease-in-out",
              cursor:
                scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            draggable={false}
          />
        </div>

        <div>
          <div className="image-carousel next">
            {/* right arrow svg */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="image-carousel-icon"
              style={{
                cursor:
                  currentImageIndex === data.images.length - 1
                    ? "not-allowed"
                    : "pointer",
                opacity: currentImageIndex === data.images.length - 1 ? 0.5 : 1,
              }}
              onClick={() => {
                if (currentImageIndex === data.images.length - 1) {
                  return;
                }
                setCurrentImageIndex(currentImageIndex + 1);
                setScale(1);
                setPosition({ x: 0, y: 0 });
              }}
            >
              <path d="m9 18 6-6-6-6" />
            </svg>
          </div>
        </div>

        <div className="popover-image-index">
          {currentImageIndex + 1} of {data.images.length}
        </div>
      </div>

      <div className="popover-footer-container">
        <div className="popover-footer" ref={containerRef}>
          {data.images.map((img, index) => (
            <div
              key={index}
              ref={(el) => (itemRefs.current[index] = el)}
              className={`popover-footer-item ${
                index === currentImageIndex ? "active" : ""
              }`}
              onClick={() => setCurrentImageIndex(index)}
            >
              <img src={img.url} alt="" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// const ImageCapture = () => {
//   const [videoState, setVideoState] = useState(false);
//   const handleOpenCamera = () => {
//     setVideoState(!videoState);
//     const media = navigator.mediaDevices.getUserMedia({
//       video: videoState,
//     });
//   };
//   const handleCapture = () => { };
//   return (
//     <div>
//       <button onClick={handleOpenCamera}>
//         {!videoState ? "Close Camera" : "Open Camera"}
//       </button>
//       <button onClick={handleCapture}>Capture</button>
//     </div>
//   );
// };
const ImageCapture = ({ onCapture, onClose }) => {
  const videoRef = useRef(null);
  const [hasPermission, setHasPermission] = useState(false);
  const [error, setError] = useState(null);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }, // Use back camera by default
        audio: false,
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setHasPermission(true);
      }
    } catch (err) {
      setError("Could not access camera. Please check permissions.");
      console.error("Camera error:", err);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !hasPermission) return;

    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (blob) {
          const file = new File([blob], `capture-${Date.now()}.jpg`, {
            type: "image/jpeg",
            lastModified: Date.now(),
          });
          onCapture([file]);
          stopCamera();
          onClose();
        }
      },
      "image/jpeg",
      0.9
    );
  };

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  return (
    <div className="camera-container">
      {error ? (
        <div className="camera-error">{error}</div>
      ) : (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            className="camera-preview"
          />
          <div className="camera-controls">
            <button
              onClick={capturePhoto}
              className="btn btn-primary capture-btn"
            >
              Take Photo
            </button>
            <button
              onClick={() => {
                stopCamera();
                onClose();
              }}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
};
